import type { RiskLevel } from "@/types/endpoints/reports/GetAiReportById";

/**
 * Цвета для уровней риска
 * Используется в RiskIndicator, ContributorsList, RiskGaugeChart
 */
export const RISK_COLORS: Record<RiskLevel, string> = {
  low: "#16A34A",     // green-600
  medium: "#CA8A04",  // yellow-600
  high: "#DC2626",    // red-600
  critical: "#DC2626", // red-600
};

/**
 * Стили для уровней риска
 * Используется в RiskIndicator
 */
export const RISK_STYLES: Record<RiskLevel, { bg: string; border: string; text: string }> = {
  low: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
  },
  medium: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-700",
  },
  high: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
  },
  critical: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
  },
};

/**
 * Цвета индикаторов для разных типов отчетов
 */
export const REPORT_INDICATOR_COLORS = {
  actions: "bg-blue-500",
  answers: "bg-green-500",
  transcribe: "bg-purple-500",
  q_density: "bg-orange-500",
  evaluation: "bg-indigo-500",
} as const;

/**
 * Порог нормы для графиков активности
 */
export const NORMAL_THRESHOLD = 2;

/**
 * Дополнительные цвета для UI
 */
export const UI_COLORS = {
  RISK_PRIMARY: "rgb(220, 38, 38)",    // Основной цвет риска
  SUCCESS_PRIMARY: "#91C000",          // Основной цвет успеха
  SUCCESS_BG: "#DBEAAB1A",            // Фон успеха (с прозрачностью)
} as const;

/**
 * Цвета для уровней риска как в RiskLevelCard
 * Используется для кругов индикаторов и границ
 */
export const RISK_LEVEL_COLORS: Record<RiskLevel, string> = {
  low: "#91C000",     // зеленый
  medium: "#EBB518",  // желтый
  high: "#F97101",    // оранжевый
  critical: "#D15B46", // красный
};

/**
 * Стили для границ и фонов по уровню риска
 */
export const RISK_LEVEL_STYLES: Record<RiskLevel, { border: string; bg: string }> = {
  low: {
    border: "#91C000",
    bg: "rgba(145, 192, 0, 0.1)", // 10% прозрачности
  },
  medium: {
    border: "#EBB518",
    bg: "rgba(235, 181, 24, 0.1)",
  },
  high: {
    border: "#F97101",
    bg: "rgba(249, 113, 1, 0.1)",
  },
  critical: {
    border: "#D15B46",
    bg: "rgba(209, 91, 70, 0.1)",
  },
};

/**
 * Tailwind классы цветов текста для уровней риска
 * Используется в TranscribeDetailDrawer и других компонентах
 */
export const RISK_TEXT_COLORS: Record<RiskLevel | string, string> = {
  critical: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-blue-600',
};

/**
 * Утилита для получения цвета текста по уровню риска
 */
export function getRiskTextColor(level: string): string {
  return RISK_TEXT_COLORS[level] || RISK_TEXT_COLORS.low;
}

/**
 * Цвета фона для уровней риска
 * Используется для бейджей и индикаторов
 */
export const RISK_BG_COLORS: Record<RiskLevel, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500',
};

/**
 * Утилита для получения цвета фона по уровню риска
 */
export function getRiskBgColor(level: string): string {
  return RISK_BG_COLORS[level as RiskLevel] || RISK_BG_COLORS.low;
}

/**
 * Определение уровня риска по баллам (как в RiskLevelCard)
 */
export function getRiskLevelFromScore(score: number): RiskLevel {
  if (score >= 80) return "low";
  if (score >= 60) return "medium";
  if (score >= 40) return "high";
  return "critical";
}

/**
 * Получение цвета по уровню риска
 */
export function getRiskLevelColor(level: RiskLevel): string {
  return RISK_LEVEL_COLORS[level];
}

/**
 * Получение стилей по уровню риска
 */
export function getRiskLevelStyles(level: RiskLevel): { border: string; bg: string } {
  return RISK_LEVEL_STYLES[level];
}
